import React, { useMemo, useState } from 'react';
import { MermaidCodeEditor } from './mermaid/MermaidCodeEditor';
import { countCodeStats, stripVelostructMetadata } from './mermaid/mermaidTextUtils';
import { useSettingsStore } from '../../stores/settingsStore';

interface MermaidModalProps {
  palette: {
    panelBg: string;
    panelBorder: string;
    panelText: string;
    panelSubText: string;
  };
  glass: React.CSSProperties;
  isOpen: boolean;
  currentMermaidExport: string;
  onClose: () => void;
  onImport: (mermaidCode: string) => void;
  onExport: () => string;
}

type MermaidMode = 'import' | 'export';

const DEFAULT_IMPORT_PLACEHOLDER = `sequenceDiagram
  participant A as Service A
  participant B as Service B
  A->>B: Request data
  B-->>A: Response`;

export function MermaidModal({
  palette,
  glass,
  isOpen,
  currentMermaidExport,
  onClose,
  onImport,
}: MermaidModalProps) {
  const [activeMode, setActiveMode] = useState<MermaidMode>('import');
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [stripMetaOnImport, setStripMetaOnImport] = useState(false);
  const [stripMetaOnExport, setStripMetaOnExport] = useState(false);
  const [softWrap, setSoftWrap] = useState(true);
  const subgraphMode = useSettingsStore(s => s.mermaidSubgraphMode ?? 'frames');
  const setMermaidSubgraphMode = useSettingsStore(s => s.setMermaidSubgraphMode);
  const nodeStyle = useSettingsStore(s => s.mermaidNodeStyle === 'shapes' ? 'shapes' : 'nodes');
  const setMermaidNodeStyle = useSettingsStore(s => s.setMermaidNodeStyle);

  const exportText = useMemo(
    () => (stripMetaOnExport ? stripVelostructMetadata(currentMermaidExport) : currentMermaidExport),
    [currentMermaidExport, stripMetaOnExport],
  );
  const codeText = activeMode === 'import' ? importText : exportText;
  const stats = countCodeStats(codeText);

  if (!isOpen) return null;

  const handleImport = () => {
    try {
      const normalizedInput = stripMetaOnImport ? stripVelostructMetadata(importText) : importText;
      if (!normalizedInput.trim()) {
        setImportError('Please enter Mermaid code to import.');
        return;
      }
      onImport(normalizedInput);
      setImportText('');
      setImportError(null);
      onClose();
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Failed to import Mermaid code.');
    }
  };

  const handlePasteFromClipboard = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      if (!clipboardText) return;
      setImportText(clipboardText);
      setImportError(null);
      setActiveMode('import');
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Clipboard access was denied.');
    }
  };

  const handleCopyCode = async () => {
    const text = activeMode === 'import' ? importText : exportText;
    await navigator.clipboard.writeText(text);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 backdrop-blur-sm px-2"
      onClick={onClose}
    >
      <div
        className="rounded-2xl shadow-2xl border overflow-hidden flex flex-col"
        style={{ ...glass, width: 'min(96vw, 1180px)', height: 'min(90vh, 860px)' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: palette.panelBorder }}>
          <div className="flex items-center gap-2">
            <span
              className="text-xs font-mono px-2 py-0.5 rounded border"
              style={{ borderColor: palette.panelBorder, color: '#818cf8' }}
            >
              &lt;/&gt;
            </span>
            <h2 style={{ color: palette.panelText }} className="text-lg font-semibold">
              Mermaid Exchange
            </h2>
          </div>
          <button onClick={onClose} className="text-lg hover:opacity-70" style={{ color: palette.panelSubText }}>
            ×
          </button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_320px]">
          <section className="min-h-0 flex flex-col p-3 gap-2">
            <div className="flex items-center justify-between text-xs">
              <div style={{ color: palette.panelSubText }} className="uppercase tracking-wider">
                {activeMode === 'import' ? 'Paste Mermaid Source' : 'Generated Mermaid Source'}
              </div>
              <div style={{ color: palette.panelSubText }}>
                {stats.lines} lines · {stats.chars} chars
              </div>
            </div>
            <MermaidCodeEditor
              value={codeText}
              readOnly={activeMode === 'export'}
              softWrap={softWrap}
              palette={palette}
              placeholder={DEFAULT_IMPORT_PLACEHOLDER}
              onChange={activeMode === 'import'
                ? (nextValue) => {
                    setImportText(nextValue);
                    setImportError(null);
                  }
                : undefined}
            />
            {importError && activeMode === 'import' && (
              <div className="px-3 py-2 text-red-400 text-xs bg-red-500/10 rounded-lg border border-red-500/20">
                {importError}
              </div>
            )}
          </section>

          <aside className="border-t md:border-t-0 md:border-l p-3 flex flex-col min-h-0 overflow-hidden" style={{ borderColor: palette.panelBorder }}>
            <div className="shrink-0">
              <div className="text-xs uppercase tracking-wider mb-2" style={{ color: palette.panelSubText }}>
                Mode
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setActiveMode('import')}
                  className="px-3 py-2 text-sm rounded-lg border transition-colors"
                  style={{
                    borderColor: activeMode === 'import' ? '#6366f1' : palette.panelBorder,
                    color: activeMode === 'import' ? '#818cf8' : palette.panelSubText,
                    background: activeMode === 'import' ? 'rgba(99,102,241,0.12)' : 'transparent',
                  }}
                >
                  Import
                </button>
                <button
                  onClick={() => setActiveMode('export')}
                  className="px-3 py-2 text-sm rounded-lg border transition-colors"
                  style={{
                    borderColor: activeMode === 'export' ? '#6366f1' : palette.panelBorder,
                    color: activeMode === 'export' ? '#818cf8' : palette.panelSubText,
                    background: activeMode === 'export' ? 'rgba(99,102,241,0.12)' : 'transparent',
                  }}
                >
                  Export
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto mt-3 pr-0.5">
              <div className="text-xs uppercase tracking-wider mb-2" style={{ color: palette.panelSubText }}>
                Options
              </div>
              <label className="flex items-start gap-2 text-xs mb-2" style={{ color: palette.panelText }}>
                <input
                  type="checkbox"
                  checked={softWrap}
                  onChange={(event) => setSoftWrap(event.target.checked)}
                  className="mt-0.5"
                />
                Soft-wrap code lines
              </label>
              {activeMode === 'import' ? (
                <>
                <label className="flex items-start gap-2 text-xs" style={{ color: palette.panelText }}>
                  <input
                    type="checkbox"
                    checked={stripMetaOnImport}
                    onChange={(event) => setStripMetaOnImport(event.target.checked)}
                    className="mt-0.5"
                  />
                  Strip `%% @velostruct:*` metadata before import
                </label>
                <div className="mt-3">
                  <div className="text-xs uppercase tracking-wider mb-2" style={{ color: palette.panelSubText }}>
                    Subgraphs
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setMermaidSubgraphMode('frames')}
                      className="px-2 py-1.5 text-xs rounded-lg border"
                      style={{
                        borderColor: subgraphMode === 'frames' ? '#6366f1' : palette.panelBorder,
                        color: subgraphMode === 'frames' ? '#818cf8' : palette.panelSubText,
                        background: subgraphMode === 'frames' ? 'rgba(99,102,241,0.12)' : 'transparent',
                      }}
                    >
                      Frames
                    </button>
                    <button
                      onClick={() => setMermaidSubgraphMode('groups')}
                      className="px-2 py-1.5 text-xs rounded-lg border"
                      style={{
                        borderColor: subgraphMode === 'groups' ? '#6366f1' : palette.panelBorder,
                        color: subgraphMode === 'groups' ? '#818cf8' : palette.panelSubText,
                        background: subgraphMode === 'groups' ? 'rgba(99,102,241,0.12)' : 'transparent',
                      }}
                    >
                      Groups
                    </button>
                  </div>
                  <p className="mt-1 text-[11px]" style={{ color: palette.panelSubText }}>
                    Frames keep contents visible like Mermaid. Groups collapse them.
                  </p>
                </div>
                </>
              ) : (
                <label className="flex items-start gap-2 text-xs" style={{ color: palette.panelText }}>
                  <input
                    type="checkbox"
                    checked={stripMetaOnExport}
                    onChange={(event) => setStripMetaOnExport(event.target.checked)}
                    className="mt-0.5"
                  />
                  Hide `%% @velostruct:*` metadata in export output
                </label>
              )}
            </div>

            <div className="shrink-0 mt-3 pt-3 border-t flex flex-col gap-2" style={{ borderColor: palette.panelBorder }}>
              {activeMode === 'import' ? (
                <>
                  <div>
                    <div className="text-xs uppercase tracking-wider mb-2" style={{ color: palette.panelSubText }}>
                      Import flowchart as
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      <button
                        type="button"
                        onClick={() => setMermaidNodeStyle('nodes')}
                        className="px-2.5 py-2 text-left rounded-lg border"
                        style={{
                          borderColor: nodeStyle === 'nodes' ? '#6366f1' : palette.panelBorder,
                          color: nodeStyle === 'nodes' ? palette.panelText : palette.panelSubText,
                          background: nodeStyle === 'nodes' ? 'rgba(99,102,241,0.12)' : 'transparent',
                        }}
                      >
                        <span className="block text-sm font-medium">Architecture nodes</span>
                        <span className="block text-[11px] mt-0.5" style={{ color: palette.panelSubText }}>
                          Service, Database, Person, Queue
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setMermaidNodeStyle('shapes')}
                        className="px-2.5 py-2 text-left rounded-lg border"
                        style={{
                          borderColor: nodeStyle === 'shapes' ? '#6366f1' : palette.panelBorder,
                          color: nodeStyle === 'shapes' ? palette.panelText : palette.panelSubText,
                          background: nodeStyle === 'shapes' ? 'rgba(99,102,241,0.12)' : 'transparent',
                        }}
                      >
                        <span className="block text-sm font-medium">Drawing shapes</span>
                        <span className="block text-[11px] mt-0.5" style={{ color: palette.panelSubText }}>
                          Box, oval, diamond, cylinder
                        </span>
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={handlePasteFromClipboard}
                      className="px-3 py-2 text-sm rounded-lg border transition-colors hover:bg-white/5"
                      style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                    >
                      Paste
                    </button>
                    <button
                      onClick={() => {
                        setImportText('');
                        setImportError(null);
                      }}
                      className="px-3 py-2 text-sm rounded-lg border transition-colors hover:bg-white/5"
                      style={{ color: palette.panelSubText, borderColor: palette.panelBorder }}
                    >
                      Clear
                    </button>
                  </div>
                  <button
                    onClick={handleImport}
                    className="px-3 py-2 text-sm rounded-lg font-medium text-white"
                    style={{ background: '#4f46e5' }}
                  >
                    Import Diagram
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleCopyCode}
                    className="px-3 py-2 text-sm rounded-lg border transition-colors hover:bg-white/5"
                    style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                  >
                    Copy Code
                  </button>
                  <button
                    onClick={onClose}
                    className="px-3 py-2 text-sm rounded-lg border transition-colors hover:bg-white/5"
                    style={{ color: palette.panelSubText, borderColor: palette.panelBorder }}
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
